import { useEffect, useState, type FormEvent } from "react";

import { Eye, EyeOff, Loader2 } from "lucide-react";
import { Link, useSearchParams } from "react-router-dom";

import { GradientScene, SceneError } from "@/components/auth/GradientScene";
import { useAuth } from "@/hooks/useAuth";
import { ApiError } from "@/lib/api-client";

/**
 * What each SSO refusal means, in words the person in front of the screen can use.
 *
 * ⚠ These are deliberately MORE specific than the password-path messages, and that is not
 * an inconsistency. Password sign-in must not reveal whether an account exists, because
 * anyone can try an address. Reaching here means Microsoft has already authenticated the
 * person against Unifiber's own tenant — so telling them "your account is not set up here"
 * reveals nothing they could not establish anyway, and saves a support call.
 */
const SSO_ERRORS: Record<string, string> = {
  not_provisioned:
    "Your Microsoft account signed in, but there is no Cocon-Stats account for that address. Ask your Unifiber administrator to create one.",
  local_only:
    "This account is set up for password sign-in rather than Microsoft. Use your email and password below.",
  inactive: "This account has been deactivated. Contact your Unifiber administrator.",
  group_denied: "Your Microsoft account is not in a group permitted to use Cocon-Stats.",
  no_email: "Microsoft did not return an email address for your account.",
  state_mismatch: "That sign-in attempt expired or did not complete. Please try again.",
  nonce_mismatch: "That sign-in attempt could not be verified. Please try again.",
  token_error: "Microsoft could not complete the sign-in. Please try again.",
  server_error: "Something went wrong starting Microsoft sign-in. Please try again.",
};

/** From mockups/01-login.html, which Ubi validated on 16 Sep 2026. */
export function Login({ ssoConfigured }: { ssoConfigured: boolean }) {
  const { login } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [params, setParams] = useSearchParams();

  // The SSO callback cannot render anything itself — it is a redirect — so it reports
  // failure as ?sso_error=<reason>. Translate the reason into something a person can act
  // on, then strip it from the URL so a reload does not resurrect a stale message.
  useEffect(() => {
    const reason = params.get("sso_error");
    if (!reason) return;
    setError(SSO_ERRORS[reason] ?? "Microsoft sign-in failed. Please try again or use your password.");
    params.delete("sso_error");
    setParams(params, { replace: true });
  }, [params, setParams]);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError("");
    setBusy(true);
    try {
      await login(email.trim(), password);
      // No navigation here. The step machine in useAuth decides what comes next, and
      // App.tsx renders it — so the client cannot route itself past the second factor.
    } catch (err) {
      if (err instanceof ApiError && err.status === 423) {
        setError("This account is temporarily locked after too many attempts. Try again shortly.");
      } else if (err instanceof ApiError && err.status === 429) {
        setError("Too many attempts. Please wait a moment and try again.");
      } else if (err instanceof ApiError) {
        // ⚠ Shown exactly as the server worded it. The server deliberately gives the same
        // message for a wrong password, an unknown address and a deactivated account —
        // do not "improve" this into something that reveals which.
        setError(err.message);
      } else {
        setError("Could not reach the server. Check your connection and try again.");
      }
      setBusy(false);
    }
  }

  return (
    <GradientScene>
      <div>
        <h2 className="text-xl font-bold text-white">Welcome back</h2>
        <p className="mt-1 text-sm text-white/60">Sign in to view your reports</p>
      </div>

      <form className="space-y-4" onSubmit={onSubmit}>
        <SceneError message={error} />

        <div>
          <label htmlFor="email" className="mb-1.5 block text-sm font-medium text-white/80">
            Email address
          </label>
          <input
            id="email"
            type="email"
            autoComplete="username"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="name@company.com"
            className="glass-input w-full rounded-lg px-4 py-2.5 text-sm"
          />
        </div>

        <div>
          <div className="mb-1.5 flex items-center justify-between">
            <label htmlFor="password" className="block text-sm font-medium text-white/80">
              Password
            </label>
            <Link
              to="/forgot-password"
              className="text-xs font-medium text-white/60 underline underline-offset-2 hover:text-white"
            >
              Forgot password?
            </Link>
          </div>
          <div className="relative">
            <input
              id="password"
              type={showPassword ? "text" : "password"}
              autoComplete="current-password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Enter your password"
              className="glass-input w-full rounded-lg px-4 py-2.5 pr-11 text-sm"
            />
            <button
              type="button"
              onClick={() => setShowPassword((v) => !v)}
              aria-label={showPassword ? "Hide password" : "Show password"}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-white/40 hover:text-white/70"
            >
              {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
            </button>
          </div>
        </div>

        <button
          type="submit"
          disabled={busy}
          className="gradient-btn flex w-full items-center justify-center gap-2 rounded-lg py-2.5 text-sm font-semibold shadow-lg disabled:cursor-not-allowed disabled:opacity-60"
        >
          {busy && <Loader2 className="h-4 w-4 animate-spin" />}
          {busy ? "Signing in…" : "Sign in"}
        </button>
      </form>

      {/* ⚠ Rendered only when the server reports SSO is actually configured (/health
          → ssoConfigured). A Microsoft button that fails after the redirect is worse than
          no button at all, and the redirect URI is still not registered. */}
      {ssoConfigured && (
        <>
          <div className="divider-text text-xs font-medium text-white/40">or</div>
          <button
            type="button"
            onClick={() => {
              window.location.href = "/internal/v1/auth/sso/login";
            }}
            className="glass-outline-btn flex w-full items-center justify-center gap-3 rounded-lg px-4 py-2.5 text-sm font-medium text-white/90 hover:text-white"
          >
            <svg width="18" height="18" viewBox="0 0 21 21" aria-hidden="true">
              <rect x="1" y="1" width="9" height="9" fill="#f25022" />
              <rect x="11" y="1" width="9" height="9" fill="#7fba00" />
              <rect x="1" y="11" width="9" height="9" fill="#00a4ef" />
              <rect x="11" y="11" width="9" height="9" fill="#ffb900" />
            </svg>
            Sign in with Microsoft
          </button>
          <p className="-mt-2 text-center text-[11px] text-white/40">Unifiber staff only</p>
        </>
      )}

      <p className="text-center text-xs text-white/35">
        No account? Contact your Unifiber administrator.
      </p>
    </GradientScene>
  );
}
